<div
  class="so-table-container"
  [class.table-minimal]="tableStyle() === 'minimal'"
  [class.so-table--dense]="density() === 'dense'"
  [class.no-border-radius]="!borderRadius()"
  [class.sync-horizontal-scroll]="syncHorizontalScroll() || hasStickyColumns()"
  [class.form-fields-classic]="formFieldsVariant() === 'classic'"
  [class]="'pb-' + (enablePagination() ? '0' : paddingBottomClass())"
  [style]="containerCustomStyle()">

  <!-- Table Header Section -->
  @if (showTableHeader()) {
    <div class="so-table-header">
      <div class="so-table-header-left">
        @if (tableHeaderIcon()) {
          <so-premium-icon-ui
            [icon]="tableHeaderIcon()"
            [color]="tableHeaderIconColor()"
            [size]="18"
            [padding]="8"
            [borderRadius]="10" />
        }
        <div class="so-table-header-titles">
          @if (tableTitle()) {
            <span class="so-table-title">{{ tableTitle() }}</span>
          }
          @if (tableSubtitle()) {
            <span class="so-table-subtitle">{{ tableSubtitle() }}</span>
          }
        </div>
      </div>
      <ng-content select="[tableHeaderExtra]"></ng-content>
      <div class="so-table-header-right">
        @if (showTableActionButton()) {
          <span [matTooltip]="tableActionButtonDisabled() ? tableActionButtonTooltip() : ''">
            <so-button-gradient-ui
              [text]="tableActionButtonText()"
              (click)="onTableActionButtonClick()"
              [icon]="tableActionButtonIcon()"
              [disabled]="tableActionButtonDisabled()"
            ></so-button-gradient-ui>
          </span>
        }
        @if (showTableSearch()) {
          <so-input-ui
            #tableSearchInput
            [placeholder]="tableSearchPlaceholder()"
            [type]="'search'"
            [inputHeight]="36"
            (input)="onSearchChange($any($event.target).value)"
          />
        }
      </div>
    </div>
  }

  <!-- Table Content -->
  <div #tableContent class="so-table-content" [style]="fixHeadCustomStyle()">
    <table
      class="so-table"
      [class.txt-wrap]="textWrap()"
      [class.no-inner-borders]="!showInnerBorders()">

      <!-- Table Head -->
      <thead #tableHead [class]="headerStyle()">
        <tr [style.height]="headersHeight()">
          @if (isActionsEnabled() && !isActionsStickyRight()) {
            <th
              class="so-table-actions-header"
              [class.sticky-left]="isActionsStickyLeft()"
              [style.left.px]="stickyLeftOffsets()['actions'] || 0"
              [style.width]="buttonsContainerWidth()"
              [style.min-width]="buttonsContainerWidth()"
              [style.paddingLeft]="buttonsContainerPaddingLeft()">
            </th>
          }

          @for (header of tableHeaders(); track header.dataName; let i = $index) {
            @if (shouldShowColumn(header)) {
              <th
                class="so-table-th"
                [attr.data-column-key]="header.dataName"
                [attr.data-col-id]="header.dataName"
                [title]="header.header"
                [class.sticky-left]="(i === firstColumnIndex() && firstColumnStickyLeft()) || stickyLeftDataNames().has(header.dataName)"
                [style.left.px]="stickyLeftDataNames().has(header.dataName) ? (stickyLeftOffsets()[header.dataName] || 0) : null"
                [class.sticky-right]="stickyRightDataNames().has(header.dataName)"
                [style.right.px]="stickyRightDataNames().has(header.dataName) ? (stickyRightOffsets()[header.dataName] || 0) : null"
                [class.sorting-active]="sortField() === header.dataName"
                [class.sortable]="header.sortable !== false && !isVariableSelectionMode() && !isVariableStatusMode()"
                [class.variable-selection-header]="isVariableSelectionMode()"
                [class.variable-status-header]="isVariableStatusMode()"
                [class.variable-status-header--mapped]="isVariableStatusMode() && isColumnMapped(header.dataName)"
                [class.variable-status-header--highlighted]="isVariableStatusMode() && highlightedColumn() === header.dataName"
                [class.text-left]="header.align === 'left'"
                [class.text-center]="header.align === 'center'"
                [class.text-right]="header.align === 'right'"
                [style.padding-left]="i === firstColumnIndex() && headerStyle() === 'new' ? cssValue(firstColumnPaddingLeft()) : null"
                [style.padding-right]="i === lastColumnIndex() ? cssValue(lastColumnPaddingRight()) : null"
                [style.color]="headerStyle() === 'new' && headersColorIsPrimary() && tableStyle() !== 'minimal' && !isVariableSelectionMode() && !isVariableStatusMode() ? 'var(--so-primary)' : null"
                [style.width]="header.columnWidth || 'auto'"
                [style.background]="isVariableSelectionMode() ? 'var(--so-gray-light)' : null"
                (click)="!isVariableSelectionMode() && !isVariableStatusMode() && onSort(header)">

                @if (isVariableStatusMode()) {
                  <div
                    class="so-table-variable-status-header"
                    (mouseenter)="onColumnHovered(header.dataName)"
                    (mouseleave)="onColumnHovered(null)">
                    @if (enableEditableHeaders()) {
                      <div class="so-table-editable-header so-table-editable-header--inline" [class.has-error]="hasHeaderInputError(header.dataName)">
                        <so-input-ui
                          [type]="'text'"
                          [placeholder]="header.header"
                          [inputHeight]="28"
                          [customStyle]="hasHeaderInputError(header.dataName) ? 'border-color: var(--so-danger);' : ''"
                          (valueChanged)="onHeaderInputChange($event, header)" />
                      </div>
                    } @else {
                      <span class="so-table-variable-status-name">{{ header.header }}</span>
                    }
                    <span
                      class="so-table-variable-status"
                      [class.so-table-variable-status--mapped]="isColumnMapped(header.dataName)">
                      <so-svg-icon-ui
                        class="so-table-variable-status-icon"
                        [name]="isColumnMapped(header.dataName) ? 'check_circle' : 'remove_circle_outline'"
                        [size]="14"
                        [color]="isColumnMapped(header.dataName) ? '--so-green' : '--so-slate-400'" />
                      <span class="so-table-variable-status-label">
                        {{ getVariableStatus(header.dataName)?.label || unmappedStatusText() }}
                      </span>
                    </span>
                  </div>
                } @else {
                  @if (enableVariableSelection()) {
                    <!-- Variable Selection Header -->
                    <div class="so-table-variable-header">
                      <so-select-ui
                        [items]="variablesCatalog()"
                        [selectedItemId]="getHeaderSelectValue(header.dataName)"
                        [disabledKeys]="takenVariables()"
                        [placeholder]="'Seleccionar'"
                        [selectHeight]="36"
                        [isFilter]="true"
                        [autoApply]="true"
                        [fullWidth]="true"
                        [clearable]="true"
                        (selectionChange)="onSelectVariable($event, header)" />
                    </div>
                  }

                  @if (enableEditableHeaders()) {
                    <!-- Editable Header Input -->
                    <div class="so-table-editable-header" [class.has-error]="hasHeaderInputError(header.dataName)">
                      <so-input-ui
                        [type]="'text'"
                        [placeholder]="header.header"
                        [inputHeight]="36"
                        [customStyle]="hasHeaderInputError(header.dataName) ? 'border-color: var(--so-danger);' : ''"
                        (valueChanged)="onHeaderInputChange($event, header)" />
                    </div>
                  } @else if (!enableVariableSelection()) {
                    <!-- Standard Header -->
                    <div class="so-table-th-content" [class.has-info]="header.columnInfoFunction">
                      @if (header.columnInfoFunction) {
                        <span
                          class="info-icon material-icons"
                          (click)="header.columnInfoFunction(); $event.stopPropagation()">
                          info
                        </span>
                      }
                      <span class="header-text">
                        @if (header.headerDotColor) {
                          <span class="so-table-header-dot" [style.background]="cssValue(header.headerDotColor)"></span>
                        }
                        @if (header.headerIcon) {
                          <so-premium-icon-ui
                            class="so-table-column-header-icon"
                            [icon]="header.headerIcon.icon"
                            [color]="header.headerIcon.color || '--so-primary'"
                            [size]="header.headerIcon.size || 12"
                            [padding]="4"
                            [borderRadius]="6" />
                        }
                        <span class="header-label">{{ headersCaseIsUpper() ? header.header.toUpperCase() : header.header }}</span>
                        @if (header.headerRequired) {
                          <span class="so-table-header-required">*</span>
                        }
                      </span>
                      @if (header.sortable !== false) {
                          <so-svg-icon-ui
                            class="sort-icon"
                            [name]="sortField() === header.dataName ? (sortDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'"
                            [size]="16"
                            [color]="sortField() === header.dataName ? 'var(--so-primary)' : 'var(--so-secondary)'" />
                      }
                    </div>
                  }
                }
              </th>
            }
          }

          @if (showSwitches()) {
            <th
              class="so-table-th so-table-switch-header"
              [class.sticky-right]="isSwitchStickyRight()"
              [style.right.px]="stickyRightOffsets()['switch'] || 0"
              [class.sortable]="isSwitchSortable()"
              [class.sorting-active]="isSwitchSortable() && sortField() === switchIdentifier()"
              [style.color]="headerStyle() === 'new' && headersColorIsPrimary() && tableStyle() !== 'minimal' ? 'var(--so-primary)' : null"
              [style.text-align]="switchAlign()"
              (click)="isSwitchSortable() && onSwitchSort()">
              <div class="so-table-th-content" [style.justify-content]="switchAlign()">
                <span class="header-text">
                  {{ headersCaseIsUpper() ? (switchHeader() || 'Switch')?.toUpperCase() : (switchHeader() || 'Switch') }}
                </span>
                @if (isSwitchSortable()) {
                  <so-svg-icon-ui
                    class="sort-icon"
                    [name]="sortField() === switchIdentifier() ? (sortDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'"
                    [size]="16"
                    [color]="sortField() === switchIdentifier() ? 'var(--so-primary)' : 'var(--so-secondary)'" />
                }
              </div>
            </th>
          }

          @if (showIcons()) {
            <th
              class="so-table-th so-table-icons-header"
              [class.sticky-right]="isIconsStickyRight()"
              [style.right.px]="stickyRightOffsets()['icons'] || 0"
              [style.color]="headerStyle() === 'new' && headersColorIsPrimary() && tableStyle() !== 'minimal' ? 'var(--so-primary)' : null">
              <div class="so-table-th-content">
                <span class="header-text">
                  {{ headersCaseIsUpper() ? iconsHeader().toUpperCase() : iconsHeader() }}
                </span>
              </div>
            </th>
          }

          @if (isActionsEnabled() && isActionsStickyRight()) {
            <th
              class="so-table-actions-header"
              [class.sticky-right]="isActionsStickyRight()"
              [style.right.px]="stickyRightOffsets()['actions'] || 0"
              [style.width]="buttonsContainerWidth()"
              [style.min-width]="buttonsContainerWidth()"
              [style.paddingLeft]="buttonsContainerPaddingLeft()">
            </th>
          }
        </tr>
      </thead>

    </table>

    <!-- Virtual Scroll Body -->
    <cdk-virtual-scroll-viewport
      class="so-table-virtual-viewport"
      [itemSize]="effectiveVirtualItemSize()"
      [minBufferPx]="virtualMinBufferPx()"
      [maxBufferPx]="virtualMaxBufferPx()"
      [style.height.px]="virtualViewportHeight()">
      <table class="so-table so-table-virtual" [class.txt-wrap]="textWrap()" [class.no-inner-borders]="!showInnerBorders()">
        <tbody [class]="headerStyle()" [class.striped]="striped()">
          <tr
            *cdkVirtualFor="let item of displayedData(); let rowIndex = index; trackBy: trackByRow"
            class="so-table-row"
            [class.selected-row]="enableRowSelection() && selectedRow() === item"
            [class.odd]="rowIndex % 2 === 1"
            [style.height]="effectiveRowsHeight()"
            [style.background]="rowColorFunction() ? rowColorFunction()!(item) : null"
            [style.box-shadow]="rowBorderFunction() ? rowBorderFunction()!(item) : null"
            [style.border-radius]="rowBorderFunction() && rowBorderFunction()!(item) ? '6px' : null"
            (click)="onRowClick(item)">

            <ng-template #actionsBodyCell>
              <td
                class="so-table-actions-cell"
                [class.sticky-left]="isActionsStickyLeft()"
                [class.sticky-right]="isActionsStickyRight()"
                [class.sticky-right-outer]="isActionsStickyRight() && isStickyRightOuter('actions')"
                [style.left.px]="isActionsStickyLeft() ? (stickyLeftOffsets()['actions'] || 0) : null"
                [style.right.px]="isActionsStickyRight() ? (stickyRightOffsets()['actions'] || 0) + scrollbarWidthPx() : null"
                [style.width]="buttonsContainerWidth()"
                [style.min-width]="buttonsContainerWidth()">
                @if (actionsDisplay() === 'menu') {
                  <so-row-actions-menu-ui
                    [actions]="rowActionButtons()"
                    [item]="item"
                    title="Acciones" />
                } @else {
                  <div class="so-table-actions">
                    @if (editAction()) {
                      <button
                        class="so-table-action-icon edit"
                        (click)="onEditRow(item, $event)"
                        title="Editar">
                        <so-svg-icon-ui name="edit" color="--so-secondary" [size]="16" />
                      </button>
                    }
                    @for (button of customButtons(); track $index) {
                        <button
                          class="so-table-action-icon custom"
                          [class.disabled]="isButtonDisabled(button, item)"
                          [class.hidden-button]="!isButtonVisible(button, item)"
                          [disabled]="isButtonDisabled(button, item)"
                          [title]="getTooltipContent(button, item)"
                          (click)="onCustomButtonClick(button, item, $event)">
                          <so-svg-icon-ui [name]="button.svg || button.icon || 'content-copy'" [color]="button.color || '--so-secondary'" [size]="16" />
                        </button>
                    }
                    @if (deleteAction()) {
                      <button
                        class="so-table-action-icon delete"
                        (click)="onDeleteRow(item, $event)"
                        title="Eliminar">
                        <so-svg-icon-ui name="delete" color="--so-danger" [size]="16" />
                      </button>
                    }
                  </div>
                }
              </td>
            </ng-template>

            <!-- Actions Column (default / sticky-left position) -->
            @if (isActionsEnabled() && !isActionsStickyRight()) {
              <ng-container *ngTemplateOutlet="actionsBodyCell"></ng-container>
            }

            <!-- Data Columns -->
            @for (header of tableHeaders(); track header.dataName; let colIndex = $index) {
              @if (shouldShowColumn(header)) {
                <td
                  class="so-table-td"
                  [attr.data-column-key]="header.dataName"
                  [class.variable-status-cell--highlighted]="isVariableStatusMode() && highlightedColumn() === header.dataName"
                  [class.sticky-left]="(colIndex === firstColumnIndex() && firstColumnStickyLeft()) || stickyLeftDataNames().has(header.dataName)"
                  [style.left.px]="stickyLeftDataNames().has(header.dataName) ? (stickyLeftOffsets()[header.dataName] || 0) : null"
                  [class.sticky-right]="stickyRightDataNames().has(header.dataName)"
                  [class.sticky-right-outer]="stickyRightDataNames().has(header.dataName) && isStickyRightOuter(header.dataName)"
                  [style.right.px]="stickyRightDataNames().has(header.dataName) ? (stickyRightOffsets()[header.dataName] || 0) + scrollbarWidthPx() : null"
                  [class.text-left]="header.align === 'left'"
                  [class.text-center]="header.align === 'center' || header.centerText"
                  [class.text-right]="header.align === 'right'"
                  [style.padding-left]="colIndex === firstColumnIndex() && headerStyle() === 'new' ? cssValue(firstColumnPaddingLeft()) : cssValue(header.paddingLeft)"
                  [style.padding-right]="colIndex === lastColumnIndex() ? cssValue(lastColumnPaddingRight()) : null"
                  [style.width]="header.columnWidth || 'auto'"
                  [style.font-weight]="header.isBold ? 'bold' : (header.isSemiBold ? '600' : 'normal')"
                  [style.color]="cssValue(header.textColor)"
                  [title]="getPropertyValue(item, header.dataName)">
                  <div class="so-table-cell-content" [style]="header.customTDStyle || ''" [class.text-center]="header.centerText || header.align === 'center'" [class.text-right]="header.align === 'right'">
                    @if (header.isButton) {
                      @if (isHeaderButtonVisible(header, item)) {
                        @if(getPropertyValue(item, header.dataName)){
                          <button
                            class="so-table-cell-button"
                            [disabled]="isHeaderButtonDisabled(header, item)"
                            (click)="onHeaderButtonClick(header, item, $event)">
                            @if (header.isButton!.svg) {
                              <so-svg-icon-ui [name]="header.isButton!.svg" [color]="header.isButton!.color || '--so-secondary'" [size]="16" />
                            }
                            @if (header.isButton!.text) {
                              <span>{{ getContentButtonText(item, header) }}</span>
                            }
                          </button>
                        }
                      }
                    } @else if (header.badge) {
                      @if (isBadgeVisible(header.badge, item)) {
                      <so-badge-ui
                        [text]="header.badge!.returnFullItem ? header.badge!.text!(item) : header.badge!.text!(getPropertyValue(item, header.dataName))"
                        [color]="getBadgeColor(header.badge!.returnFullItem ? header.badge!.color!(item) : header.badge!.color!(getPropertyValue(item, header.dataName)))"
                        [colors]="getBadgeColors(header.badge.colors && header.badge.colors(item))"
                        size="sm" />
                      }
                    } @else if (header.convertFunction) {
                      <span class="so-table-cell-text">{{ header.convertFunction(getPropertyValue(item, header.dataName), item) }}</span>
                    } @else if (header.isDate) {
                      <span class="so-table-cell-text">{{ getPropertyValue(item, header.dataName) | date: (header.showHours ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy') }}</span>
                    } @else if (header.isUppercase) {
                      <span class="so-table-cell-text">{{ getPropertyValue(item, header.dataName)?.toString().toUpperCase() }}</span>
                    } @else if(header.isIcon) {
                      @if (header.isIcon.show(item)) {
                      <so-svg-icon-ui
                        [name]="header.isIcon.name(item)"
                        [size]="(header.isIcon.size && header.isIcon.size(item)) ?? 24"
                        [color]="(header.isIcon.color && header.isIcon.color(item)) ?? ''"
                      />
                      }
                    } @else if(header.isComponent) {
                      @if (!header.isComponent.show || header.isComponent.show(item)) {
                        <ng-container
                          *ngComponentOutlet="header.isComponent.class;
                          inputs: (header.isComponent.inputs && header.isComponent.inputs(item)) ?? {}"
                        />
                      }
                    } @else if (header.highlightText) {
                      @for (part of getHighlightedParts(item, header); track $index) {@if (part.highlighted) {<span
                        [style.color]="cssValue(header.highlightText.color)"
                        [class.so-table-highlight-bold]="header.highlightText.bold"
                        [class.so-table-highlight-semibold]="header.highlightText.semiBold"
                        [class.so-table-highlight-underline]="header.highlightText.underline === 'always'"
                        [class.so-table-highlight-underline-hover]="header.highlightText.underline === 'onHover'"
                        [class.so-table-highlight-clickable]="!!header.highlightText.action"
                        (click)="header.highlightText.action ? onHighlightClick(item, header, $event) : null"
                      >{{ part.text }}</span>} @else {<span>{{ part.text }}</span>}
                      }
                    } @else if (header.isLink) {
                      @if (isLinkVisible(header, item)) {
                        <button
                          type="button"
                          class="so-table-link"
                          [attr.aria-label]="getLinkAriaLabel(header, item)"
                          (click)="onLinkClick(header, item, $event)">
                          {{ getPropertyValue(item, header.dataName) }}
                        </button>
                      } @else {
                        <span class="so-table-cell-text">{{ getPropertyValue(item, header.dataName) }}</span>
                      }
                    } @else {
                      <span class="so-table-cell-text">{{ getPropertyValue(item, header.dataName) }}</span>
                    }
                  </div>
                </td>
              }
            }

            <!-- Switch Column -->
            @if (showSwitches()) {
              <td
                class="so-table-switch-cell"
                [class.sticky-right]="isSwitchStickyRight()"
                [class.sticky-right-outer]="isSwitchStickyRight() && isStickyRightOuter('switch')"
                [style.right.px]="isSwitchStickyRight() ? (stickyRightOffsets()['switch'] || 0) + scrollbarWidthPx() : null"
                [matTooltip]="getSwitchTooltip(item)">
                <div class="so-table-switch-cell-inner" [style.justify-content]="switchAlign()">
                  @if (isSwitchVisible(item)) {
                    <so-switch-ui
                      [labels]="true"
                      [labelLeft]="switchLabelLeft()"
                      [labelRight]="switchLabelRight()"
                      [isChecked]="getSwitchStatus(item)"
                      [disabled]="isSwitchDisabled(item)"
                      [customStyleOption]="'verde'"
                      (statusChecked)="onSwitchChangeFromSwitch(item, $event)"
                      (click)="$event.stopPropagation()" />
                  }
                </div>
              </td>
            }

            <!-- Icons Column -->
            @if (showIcons()) {
              <td
                class="so-table-icons-cell"
                [class.sticky-right]="isIconsStickyRight()"
                [class.sticky-right-outer]="isIconsStickyRight() && isStickyRightOuter('icons')"
                [style.right.px]="isIconsStickyRight() ? (stickyRightOffsets()['icons'] || 0) + scrollbarWidthPx() : null">
                <div class="so-table-icons-wrapper">
                  @for (icon of (item[iconsIdentifier()] || []); track $index) {
                    <so-svg-icon-ui
                      [name]="icon.icon"
                      [color]="icon.iconColor || ''"
                      [size]="resolveIconSizeNum(icon.iconSize)"
                      [tooltip]="icon.tooltipText || ''"
                    />
                  }
                </div>
              </td>
            }

            <!-- Actions Column (sticky-right position) -->
            @if (isActionsEnabled() && isActionsStickyRight()) {
              <ng-container *ngTemplateOutlet="actionsBodyCell"></ng-container>
            }
          </tr>
        </tbody>
      </table>
      @if (displayedData().length === 0) {
        <div class="so-table-empty-virtual">Sin resultados</div>
      }
    </cdk-virtual-scroll-viewport>

    <!-- Data footer -->
    @if (tableFeet()) {
    @let item = tableFeet();
    <table class="so-table so-table-tfoot">
      <tfoot #tfoot class="so-table-footer" style="padding: 0;">
        <tr>
        @for (header of tableHeaders(); track header.dataName; let i = $index) {
          @if (shouldShowColumn(header)){
          <td
            class="so-table-th"
            [attr.data-column-key]="header.dataName"
            [class.variable-status-cell--highlighted]="isVariableStatusMode() && highlightedColumn() === header.dataName"
            style="border-bottom: 1px solid var(--so-transparent); padding: 12px;"
            [style.color]="headerStyle() === 'new' && headersColorIsPrimary() && tableStyle() !== 'minimal' && !enableVariableSelection() ? 'var(--so-primary)' : null"
            [class.sticky-left]="(i === firstColumnIndex() && firstColumnStickyLeft()) || stickyLeftDataNames().has(header.dataName)"
            [style.left.px]="stickyLeftDataNames().has(header.dataName) ? (stickyLeftOffsets()[header.dataName] || 0) : null"
            [class.sticky-right]="stickyRightDataNames().has(header.dataName)"
            [style.right.px]="stickyRightDataNames().has(header.dataName) ? (stickyRightOffsets()[header.dataName] || 0) : null"
            [style.padding-left]="i === firstColumnIndex() && headerStyle() === 'new' ? cssValue(firstColumnPaddingLeft()) : cssValue(header.paddingLeft)"
            [style.padding-right]="i === lastColumnIndex() ? cssValue(lastColumnPaddingRight()) : null"
            [style.width]="header.columnWidth || 'auto'"
          >
            <div class="so-table-cell-content" [style]="header.customTDStyle || ''" [class.text-center]="header.centerText || header.align === 'center'" [class.text-right]="header.align === 'right'">
            @if (header.isButton) {
              @if (isHeaderButtonVisible(header, item)) {
                <button
                  class="so-table-cell-button"
                  [disabled]="isHeaderButtonDisabled(header, item)"
                  (click)="onHeaderButtonClick(header, item, $event)">
                  @if (header.isButton!.svg) {
                    <so-svg-icon-ui [name]="header.isButton!.svg" [color]="header.isButton!.color || '--so-secondary'" [size]="16" />
                  }
                  @if (header.isButton!.text) {
                    <span>{{ header.isButton!.text }}</span>
                  }
                  {{ getPropertyValue(item, header.dataName) }}
                </button>
              }
            } @else if (header.badge) {
              @if (isBadgeVisible(header.badge, item)) {
              <so-badge-ui
                [text]="header.badge!.returnFullItem ? header.badge!.text!(item) : header.badge!.text!(getPropertyValue(item, header.dataName))"
                [color]="getBadgeColor(header.badge!.returnFullItem ? header.badge!.color!(item) : header.badge!.color!(getPropertyValue(item, header.dataName)))"
                [colors]="getBadgeColors(header.badge.colors && header.badge.colors(item))"
                size="sm" />
              }
            } @else if (header.convertFunction) {
              {{ header.convertFunction(getPropertyValue(item, header.dataName), item) }}
            } @else if (header.isDate) {
              {{ getPropertyValue(item, header.dataName) | date: (header.showHours ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy') }}
            } @else if (header.isUppercase) {
              {{ getPropertyValue(item, header.dataName)?.toString().toUpperCase() }}
            } @else if(header.isIcon) {
              @if (header.isIcon.show(item)) {
              <so-svg-icon-ui
                [name]="header.isIcon.name(item)"
                [size]="(header.isIcon.size && header.isIcon.size(item)) ?? 24"
                [color]="(header.isIcon.color && header.isIcon.color(item)) ?? ''"
              />
              }
            } @else if(header.isComponent) {
              @if (!header.isComponent.show || header.isComponent.show(item)) {
              <span [style.paddingLeft]="'6px'">
                <ng-container
                *ngComponentOutlet="header.isComponent.class;
                  inputs: (header.isComponent.inputs && header.isComponent.inputs(item)) ?? {}"
                />
              </span>
              }
            } @else {
              {{ getPropertyValue(item, header.dataName) }}
            }
            </div>
          }}
        </tr>
      </tfoot>
    </table>
    }
  </div>

  <!-- Pagination & Actions Footer -->
  @if (enablePagination() || enableSaveButton()) {
    <div class="so-table-footer" [class.has-left-button]="enableLeftButton()">
      @if (enableLeftButton()) {
        <so-button-gradient-ui
          variant="outline"
          [color]="leftButtonData().color ?? 'gray'"
          [text]="leftButtonData().text"
          [icon]="leftButtonData().icon"
          [disabled]="leftButtonData().disabled"
          [attr.title]="leftButtonData().disabled ? leftButtonData().tooltip : ''"
          (clicked)="onLeftButtonClick()"
        />
      }

      @if (enablePagination()) {
        @if (paginationType() === 'classic') {
          <div class="so-table-classic-pagination">
            <div class="so-table-pagination-info">
              <span>Mostrando <strong>{{ paginationInfo().start }}</strong> - <strong>{{ paginationInfo().end }}</strong> de <strong>{{ paginationInfo().total }}</strong></span>
            </div>
            <so-pagination-ui
              [totalItems]="totalItems()"
              [itemsPerPage]="itemsPerPage()"
              [currentPage]="internalCurrentPage()"
              [loadingItems]="loadingItems()"
              [prevDisabled]="internalCurrentPage() <= 1"
              [nextDisabled]="internalCurrentPage() >= totalPages()"
              [paddingEnd]="'0'"
              [showText]="false"
              (pageChanged)="onPageChange($event)" />
          </div>
        } @else {
          <so-token-pagination-ui
            [tokenPaginationState]="tokenPaginationState()"
            [itemsPerPage]="itemsPerPage()"
            [pageSizeOptions]="pageSizeOptions()"
            [totalItems]="totalItems()"
            (nextPageRequested)="nextPageRequested.emit($event)"
            (previousPageRequested)="previousPageRequested.emit($event)"
            (pageSizeChanged)="pageSizeChanged.emit($event)" />
        }
      }

      @if (enableSaveButton()) {
        <button class="so-table-save-btn" (click)="onSave()">
          <span class="material-icons">save</span>
          Guardar
        </button>
      }
    </div>
  }
</div>
